import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  BookOpen, 
  MapPin, 
  FileText,
  BookMarked,
  Bookmark,
  BookmarkCheck,
  StickyNote,
  History,
  Save,
  Loader2,
  Play,
  X
} from 'lucide-react';
import { quranApi, userApi, lexicalApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAudio } from '../context/AudioContext';
import { usePreferences } from '../context/PreferencesContext';
import { useTajwidExercise } from '../hooks/useTajwidExercise';
import { QuranWordRenderer } from '../components/lexical/QuranWordRenderer';
import { QuranWordAnalysisPanel } from '../components/lexical/QuranWordAnalysisPanel';
import FontSizeControl from '../components/FontSizeControl';
import { Palette, Target, RotateCcw, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { 
  SectionHeader, 
  PageLoading, 
  ArabicVerse, 
  ContentBadge,
  GlassCard 
} from '../components/shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TafsirPanel } from '../components/TafsirPanel';
import { RelatedContent } from '../components/RelatedContent';
import { toast } from 'sonner';

const TAB_STORAGE_KEY = 'noor-albayan:verse-panel-tab';

const SurahDetail = () => {
  const { surahNumber } = useParams();
  const { user } = useAuth();
  const { playSurah, playAyah, state: audioState } = useAudio();
  const { preferences, setEnableTajwidColoring, setEnableTajwidExercise } = usePreferences();
  const [surah, setSurah] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [verseDetail, setVerseDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // --- Ajout: scroll automatique sur le verset audio courant ---
  useEffect(() => {
    if (!audioState?.currentTrack?.ayahKey) return;
    const verse = verses.find(v => v.verse_key === audioState.currentTrack.ayahKey);
    if (!verse) return;
    const el = document.getElementById(`verse-${verse.verse_number}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  }, [audioState?.currentTrack?.ayahKey, verses]);

  // Lexical word-analysis panel
  const [lexicalSelection, setLexicalSelection] = useState(null);  // {ayahKey, wordIndex, segments}
  const handleWordClick = useCallback((sel) => setLexicalSelection(sel), []);

  // Tajwīd annotations cache (ayahKey → annotations[]). Populated lazily when
  // `enableTajwidColoring` is true so we never block initial render.
  const [tajwidByAyah, setTajwidByAyah] = useState({});
  useEffect(() => {
    if (!preferences.enableTajwidColoring) return;
    if (!verses.length) return;
    let cancelled = false;
    const missing = verses
      .map((v) => v.verse_key)
      .filter((k) => k && !(k in tajwidByAyah));
    if (!missing.length) return;
    Promise.all(
      missing.map((k) =>
        lexicalApi.getAyahTajwid(k).then(
          (r) => [k, r.data?.annotations || []],
          () => [k, []],
        )
      )
    ).then((entries) => {
      if (cancelled) return;
      setTajwidByAyah((prev) => {
        const next = { ...prev };
        for (const [k, ann] of entries) next[k] = ann;
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences.enableTajwidColoring, verses]);

  // Tajwīd exercise mode (per-surah QCM)
  const exercise = useTajwidExercise(
    Number(surahNumber),
    !!preferences.enableTajwidExercise,
  );

  // Persisted active tab — survives verse-to-verse navigation
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' && window.localStorage.getItem(TAB_STORAGE_KEY);
      return saved && ['context', 'tafsir', 'hadith'].includes(saved) ? saved : 'tafsir';
    } catch {
      return 'tafsir';
    }
  });
  const changeTab = useCallback((value) => {
    setActiveTab(value);
    try { window.localStorage.setItem(TAB_STORAGE_KEY, value); } catch {}
  }, []);

  // Mobile bottom-sheet state (opens whenever a verse is selected on small screens)
  const [mobileOpen, setMobileOpen] = useState(false);

  // User-specific state
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingBookmark, setSavingBookmark] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // User tafsir preferences (applied to TafsirPanel default selection)
  const userPrefs = user?.preferences?.tafsir || {};

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [surahRes, versesRes] = await Promise.all([
          quranApi.getSurah(parseInt(surahNumber)),
          quranApi.getVerses(parseInt(surahNumber))
        ]);
        setSurah(surahRes.data);
        setVerses(versesRes.data);
      } catch (error) {
        console.error('Error fetching surah:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [surahNumber]);

  // Auto-focus verse from URL params (verse=X or hash #verse-X)
  useEffect(() => {
    if (verses.length === 0) return;
    
    // Check search params (verse=X) or hash (#verse-X)
    const verseParam = searchParams.get('verse') || 
                       location.hash.replace('#verse-', '');
    
    if (verseParam) {
      const verseNum = parseInt(verseParam);
      if (isNaN(verseNum)) return;
      
      // Find and scroll to the verse (from lexical page or direct link)
      const verse = verses.find(v => v.verse_number === verseNum);
      if (verse) {
        setTimeout(() => {
          setSelectedVerse(verse);
          // Don't open mobile sheet — just show verse on page directly
          // (User can manually tap verse to open detail panel if needed)
          // Scroll into view with smooth behavior
          const element = document.getElementById(`verse-${verseNum}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus({ preventScroll: true });
          }
        }, 100);
      }
    }
  }, [verses, searchParams, location.hash]);


  // Fetch user's bookmarks/notes (only if logged-in) — filtered to this surah
  useEffect(() => {
    if (!user) {
      setBookmarks([]);
      setNotes([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      userApi.getBookmarks().catch(() => ({ data: [] })),
      userApi.getNotes().catch(() => ({ data: [] })),
    ]).then(([b, n]) => {
      if (cancelled) return;
      const sn = parseInt(surahNumber);
      const filterBySurah = (x) =>
        x.content_type === 'quran' &&
        x.content_id &&
        x.content_id.startsWith(`${sn}:`);
      setBookmarks((b.data || []).filter(filterBySurah));
      setNotes((n.data || []).filter(filterBySurah));
    });
    return () => { cancelled = true; };
  }, [user, surahNumber]);

  const verseKey = (v) => `${v.surah_number}:${v.verse_number}`;
  const bookmarkForSelected = selectedVerse
    ? bookmarks.find((b) => b.content_id === verseKey(selectedVerse))
    : null;
  const noteForSelected = selectedVerse
    ? notes.find((n) => n.content_id === verseKey(selectedVerse))
    : null;

  const handleVerseClick = async (verse) => {
    setSelectedVerse(verse);
    // Open mobile bottom sheet (no-op on desktop due to md:block override)
    setMobileOpen(true);
    setLoadingDetail(true);
    setShowNoteEditor(false);
    setNoteDraft('');
    try {
      const response = await quranApi.getVerseDetail(verse.surah_number, verse.verse_number);
      setVerseDetail(response.data);
    } catch (error) {
      console.error('Error fetching verse detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleToggleBookmark = useCallback(async () => {
    if (!user) {
      toast.info('Connectez-vous pour sauvegarder des versets');
      return;
    }
    if (!selectedVerse) return;
    setSavingBookmark(true);
    try {
      if (bookmarkForSelected) {
        await userApi.deleteBookmark(bookmarkForSelected.id);
        setBookmarks((prev) => prev.filter((b) => b.id !== bookmarkForSelected.id));
        toast.success('Retiré des favoris');
      } else {
        const res = await userApi.createBookmark({
          content_type: 'quran',
          content_id: verseKey(selectedVerse),
        });
        setBookmarks((prev) => [res.data, ...prev.filter((b) => b.id !== res.data.id)]);
        toast.success('Ajouté aux favoris');
      }
    } catch (e) {
      toast.error('Action impossible, réessayez');
    } finally {
      setSavingBookmark(false);
    }
  }, [user, selectedVerse, bookmarkForSelected]);

  const handleOpenNote = () => {
    if (!user) {
      toast.info('Connectez-vous pour ajouter une note');
      return;
    }
    setNoteDraft(noteForSelected?.content || '');
    setShowNoteEditor(true);
  };

  const handleSaveNote = useCallback(async () => {
    if (!user || !selectedVerse) return;
    const content = noteDraft.trim();
    if (!content) {
      toast.error('La note ne peut pas être vide');
      return;
    }
    setSavingNote(true);
    try {
      if (noteForSelected) {
        await userApi.updateNote(noteForSelected.id, {
          content_type: 'quran',
          content_id: verseKey(selectedVerse),
          content,
        });
        setNotes((prev) =>
          prev.map((n) =>
            n.id === noteForSelected.id ? { ...n, content } : n,
          ),
        );
        toast.success('Note mise à jour');
      } else {
        const res = await userApi.createNote({
          content_type: 'quran',
          content_id: verseKey(selectedVerse),
          content,
        });
        setNotes((prev) => [res.data, ...prev]);
        toast.success('Note ajoutée');
      }
      setShowNoteEditor(false);
    } catch (e) {
      toast.error('Échec de l\'enregistrement');
    } finally {
      setSavingNote(false);
    }
  }, [user, selectedVerse, noteDraft, noteForSelected]);

  const handleDeleteNote = useCallback(async () => {
    if (!noteForSelected) return;
    try {
      await userApi.deleteNote(noteForSelected.id);
      setNotes((prev) => prev.filter((n) => n.id !== noteForSelected.id));
      setShowNoteEditor(false);
      setNoteDraft('');
      toast.success('Note supprimée');
    } catch (e) {
      toast.error('Échec de la suppression');
    }
  }, [noteForSelected]);

  if (loading) return <PageLoading />;
  if (!surah) return <div className="text-center py-20 text-text-muted">Sourate non trouvée</div>;

  const prevSurah = parseInt(surahNumber) > 1 ? parseInt(surahNumber) - 1 : null;
  const nextSurah = parseInt(surahNumber) < 114 ? parseInt(surahNumber) + 1 : null;

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <div className="relative py-16 px-4 bg-gradient-to-b from-surface to-background">
        <div 
          className="absolute inset-0 opacity-10 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1768913652690-8f9e219bc2e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxhcmFiaWMlMjBjYWxsaWdyYXBoeSUyMGFic3RyYWN0fGVufDB8fHx8MTc3NTQ2OTA0OHww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Link to="/quran" className="inline-flex items-center text-text-muted hover:text-primary mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour au Coran
          </Link>
          
          <div className="mb-4">
            <span className="overline">Sourate {surah.number}</span>
          </div>
          
          <h1 className="font-arabic text-5xl sm:text-6xl text-primary mb-4" dir="rtl">
            {surah.name_arabic}
          </h1>
          <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-2">
            {surah.name_french || surah.name_english}
          </h2>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm text-text-muted">
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {surah.verses_count} versets
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {surah.revelation_place === 'Meccan' ? 'Mecquoise' : 'Médinoise'}
            </span>
          </div>
          
          {surah.summary && (
            <p className="mt-6 text-text-secondary max-w-2xl mx-auto">{surah.summary}</p>
          )}
          
          {surah.themes && surah.themes.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {surah.themes.map(theme => (
                <span 
                  key={theme}
                  className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary"
                >
                  {theme}
                </span>
              ))}
            </div>
          )}

          {/* Lire la sourate */}
          <div className="mt-6 flex justify-center">
            <Button
              onClick={() => playSurah(Number(surah.number))}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid="play-surah-button"
            >
              <Play className="w-4 h-4 mr-1.5" /> Lire la sourate
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="sticky top-16 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        {/* Top row: prev / count / font-size / next */}
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center gap-2">
          {prevSurah ? (
            <Link to={`/quran/${prevSurah}`}>
              <Button variant="ghost" size="sm" className="text-text-secondary"
                      data-testid="surah-prev-button">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédente
              </Button>
            </Link>
          ) : <div />}

          <span className="text-sm text-text-muted hidden sm:inline">
            {verses.length} versets
          </span>

          <FontSizeControl
            prefKey="quranArabicFontSize"
            testid="surah-font-size-control"
            className="ml-auto sm:ml-2 sm:mr-2"
          />

          {nextSurah ? (
            <Link to={`/quran/${nextSurah}`}>
              <Button variant="ghost" size="sm" className="text-text-secondary"
                      data-testid="surah-next-button">
                Suivante
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          ) : <div />}
        </div>

        {/* Thin secondary row: Tajwīd toggles. Kept on its own line so the
            primary nav row stays uncluttered on mobile. */}
        <div
          className="max-w-7xl mx-auto px-4 py-1.5 border-t border-border/40 flex justify-end items-center gap-2"
          data-testid="surah-tajwid-bar"
        >
          <span className="text-[11px] uppercase tracking-wider text-text-muted hidden sm:inline mr-1">
            Tajwīd
          </span>
          <button
            type="button"
            onClick={() => setEnableTajwidColoring(!preferences.enableTajwidColoring)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${
              preferences.enableTajwidColoring
                ? 'border-primary/60 bg-primary/15 text-primary'
                : 'border-border bg-surface-elevated/60 text-text-muted hover:text-text-primary'
            }`}
            data-testid="surah-tajwid-toggle"
            aria-pressed={preferences.enableTajwidColoring}
            title="Activer/désactiver la coloration Tajwīd"
          >
            <Palette className="w-3.5 h-3.5" />
            Coloration
          </button>

          <button
            type="button"
            onClick={() => setEnableTajwidExercise(!preferences.enableTajwidExercise)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${
              preferences.enableTajwidExercise
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                : 'border-border bg-surface-elevated/60 text-text-muted hover:text-text-primary'
            }`}
            data-testid="surah-exercise-toggle"
            aria-pressed={preferences.enableTajwidExercise}
            title="Mode exercice : tapotez les mots pour identifier la règle Tajwīd"
          >
            <Target className="w-3.5 h-3.5" />
            Exercice
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {preferences.enableTajwidExercise && (
          <div
            className="mb-6 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between flex-wrap gap-3"
            data-testid="tajwid-exercise-score-panel"
          >
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm text-text-primary">
                  Mode exercice — identifiez la règle Tajwīd de chaque mot mis en évidence
                </p>
                <p className="text-xs text-text-muted">
                  {exercise.loading
                    ? 'Chargement des cibles…'
                    : `${exercise.stats.answered} / ${exercise.stats.total} mots traités · ${exercise.stats.correct} bonnes réponses (${exercise.stats.percent}%)`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="text-2xl font-mono text-emerald-300"
                data-testid="tajwid-exercise-score-value"
              >
                {exercise.stats.correct}/{exercise.stats.total}
              </span>
              <Link to={`/cours/tajwid/leaderboard?surah=${surahNumber}`}>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  data-testid="tajwid-exercise-open-leaderboard"
                  title="Voir le classement sur cette sourate"
                >
                  <Trophy className="w-3 h-3 mr-1" />
                  Classement
                </Button>
              </Link>
              {exercise.stats.answered > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exercise.resetSurahScore}
                  className="text-xs"
                  data-testid="tajwid-exercise-reset"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Recommencer
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Verses List */}
          <div className="lg:col-span-2 space-y-4">
            {verses.map((verse, index) => (
              <motion.div
                key={verse.verse_number}
                id={`verse-${verse.verse_number}`}
                tabIndex={-1} // Permet le focus programmatique
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                onClick={() => handleVerseClick(verse)}
                className={`glass-card p-6 cursor-pointer transition-all duration-300 ${
                  selectedVerse?.verse_number === verse.verse_number 
                    ? 'verse-active' 
                    : 'hover:border-primary/20'
                } ${
                  audioState.currentTrack?.ayahKey === verse.verse_key
                    ? 'ring-2 ring-primary/60'
                    : ''
                }`}
                data-testid={`verse-${verse.verse_number}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary text-sm font-medium">{verse.verse_number}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        playAyah({
                          surahNumber: verse.surah_number,
                          ayahNumber: verse.verse_number,
                          ayahKey: verse.verse_key,
                          globalAyahNumber: verse.global_ayah_number,
                          hizbNumber: verse.hizb_number,
                        });
                      }}
                      className="p-1.5 rounded-full text-text-muted hover:text-primary hover:bg-primary/10 transition-colors"
                      aria-label="Lire ce verset"
                      data-testid={`verse-play-${verse.verse_number}`}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1">
                    <QuranWordRenderer
                      surahNumber={Number(surahNumber)}
                      ayahKey={verse.verse_key}
                      text={verse.arabic_text}
                      enabled={preferences.enableWordAnalysis}
                      onWordClick={handleWordClick}
                      tajwidAnnotations={
                        preferences.enableTajwidColoring
                          ? (tajwidByAyah[verse.verse_key] || [])
                          : null
                      }
                      exercise={preferences.enableTajwidExercise ? exercise : null}
                      className="mb-4"
                    />
                    
                    {verse.transliteration && (
                      <p className="text-text-muted italic text-sm mb-3">{verse.transliteration}</p>
                    )}
                    
                    <p className="text-text-primary leading-relaxed mb-2">{verse.translation_french}</p>
                    <p className="text-text-secondary text-sm">{verse.translation_english}</p>
                  </div>
                </div>
                
                {verse.themes && verse.themes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 pl-14">
                    {verse.themes.map(theme => (
                      <span key={theme} className="text-xs px-2 py-0.5 rounded bg-surface-elevated text-text-muted">
                        {theme}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Detail Panel
              - Desktop: sticky sidebar (lg:col-span-1)
              - Mobile: slide-up fixed overlay (opens on verse click, with backdrop)
          */}
          {/* Mobile backdrop (only visible when panel is open on mobile) */}
          {selectedVerse && mobileOpen && (
            <div
              className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
              data-testid="verse-panel-backdrop"
            />
          )}

          <motion.div
            className={`lg:col-span-1 ${
              selectedVerse && mobileOpen
                ? 'fixed inset-x-0 bottom-0 z-50 max-h-[88vh] lg:static lg:z-auto lg:max-h-none'
                : 'hidden lg:block'
            }`}
            initial={false}
            animate={
              selectedVerse && mobileOpen
                ? { y: 0, opacity: 1 }
                : { y: 0, opacity: 1 }
            }
          >
            <div className="sticky top-32 lg:rounded-none rounded-t-2xl overflow-hidden lg:overflow-visible shadow-2xl lg:shadow-none">
              {/* Mobile drag handle + close */}
              {selectedVerse && (
                <div className="lg:hidden flex items-center justify-between px-4 pt-5 pb-2 bg-surface border-b border-border" style={{paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))'}}>
                  <div className="mx-auto w-10 h-1 rounded-full bg-border absolute left-1/2 -translate-x-1/2 top-1.5" />
                  <span className="text-sm font-medium text-text-primary">
                    Verset {selectedVerse.verse_number}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMobileOpen(false)}
                    className="h-8 w-8 text-text-muted"
                    data-testid="verse-panel-close"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              {selectedVerse ? (
                <GlassCard className="p-0 overflow-hidden max-h-[85vh] lg:max-h-none overflow-y-auto" data-testid="verse-detail-panel">
                  <div className="p-4 bg-primary/10 border-b border-border flex items-center justify-between gap-3">
                    <h3 className="font-serif text-lg text-text-primary">
                      Verset {selectedVerse.verse_number}
                    </h3>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleToggleBookmark}
                        disabled={savingBookmark}
                        title={bookmarkForSelected ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        className={`h-9 w-9 ${
                          bookmarkForSelected
                            ? 'text-primary bg-primary/10 hover:bg-primary/20'
                            : 'text-text-muted hover:text-primary'
                        }`}
                        data-testid="bookmark-toggle-btn"
                      >
                        {savingBookmark ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : bookmarkForSelected ? (
                          <BookmarkCheck className="w-4 h-4" />
                        ) : (
                          <Bookmark className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleOpenNote}
                        title={noteForSelected ? 'Modifier la note' : 'Ajouter une note'}
                        className={`h-9 w-9 ${
                          noteForSelected
                            ? 'text-primary bg-primary/10 hover:bg-primary/20'
                            : 'text-text-muted hover:text-primary'
                        }`}
                        data-testid="note-open-btn"
                      >
                        <StickyNote className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Note editor */}
                  {showNoteEditor && (
                    <div className="p-4 border-b border-border bg-surface-elevated/60" data-testid="note-editor">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-wider text-text-muted">
                          {noteForSelected ? 'Modifier votre note' : 'Nouvelle note'}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowNoteEditor(false)}
                          className="h-7 w-7 text-text-muted"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <Textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder="Votre réflexion sur ce verset..."
                        rows={4}
                        className="bg-surface border-border text-sm"
                        data-testid="note-textarea"
                        autoFocus
                      />
                      <div className="flex items-center justify-between gap-2 mt-3">
                        {noteForSelected ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDeleteNote}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            data-testid="note-delete-btn"
                          >
                            Supprimer
                          </Button>
                        ) : (
                          <span />
                        )}
                        <Button
                          size="sm"
                          onClick={handleSaveNote}
                          disabled={savingNote || !noteDraft.trim()}
                          className="bg-primary text-background hover:bg-primary-hover"
                          data-testid="note-save-btn"
                        >
                          {savingNote ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Enregistrer
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Saved note indicator (when editor closed) */}
                  {!showNoteEditor && noteForSelected && (
                    <div
                      className="p-3 border-b border-border bg-surface-elevated/40 cursor-pointer hover:bg-surface-elevated/70 transition-colors"
                      onClick={handleOpenNote}
                      data-testid="saved-note-preview"
                    >
                      <div className="flex items-start gap-2">
                        <StickyNote className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-text-secondary line-clamp-2 italic">
                          {noteForSelected.content}
                        </p>
                      </div>
                    </div>
                  )}

                  {loadingDetail ? (
                    <div className="p-8 text-center">
                      <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
                    </div>
                  ) : verseDetail ? (
                    <Tabs value={activeTab} onValueChange={changeTab} className="p-4">
                      <TabsList className="grid w-full grid-cols-3 bg-surface-elevated">
                        <TabsTrigger value="context" className="text-xs" data-testid="tab-context">Contexte</TabsTrigger>
                        <TabsTrigger value="tafsir" className="text-xs" data-testid="tab-tafsir">Tafsir</TabsTrigger>
                        <TabsTrigger value="hadith" className="text-xs" data-testid="tab-hadith">Hadith</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="context" className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto">
                        {verseDetail.context ? (
                          <>
                            {verseDetail.context.sabab_al_nuzul && (
                              <div className="p-3 bg-surface-elevated rounded-lg">
                                <h4 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
                                  <History className="w-4 h-4 text-primary" />
                                  Raison de la révélation
                                </h4>
                                <p className="text-sm text-text-secondary leading-relaxed">{verseDetail.context.sabab_al_nuzul}</p>
                              </div>
                            )}
                            {verseDetail.context.historical_context && (
                              <div className="p-3 bg-surface-elevated rounded-lg">
                                <h4 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
                                  <BookOpen className="w-4 h-4 text-primary" />
                                  Contexte historique
                                </h4>
                                <p className="text-sm text-text-secondary leading-relaxed">{verseDetail.context.historical_context}</p>
                              </div>
                            )}
                            {verseDetail.context.certainty_level && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-text-muted">Certitude :</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  verseDetail.context.certainty_level === 'established' ? 'bg-emerald-900/30 text-emerald-400' :
                                  verseDetail.context.certainty_level === 'commonly_accepted' ? 'bg-blue-900/30 text-blue-400' :
                                  'bg-orange-900/30 text-orange-400'
                                }`}>
                                  {verseDetail.context.certainty_level === 'established' ? 'Établi' :
                                   verseDetail.context.certainty_level === 'commonly_accepted' ? 'Communément accepté' : 'Débattu'}
                                </span>
                              </div>
                            )}
                            {verseDetail.context.sources && verseDetail.context.sources.length > 0 && (
                              <div className="pt-3 border-t border-border">
                                <h5 className="text-xs text-text-muted uppercase tracking-wider mb-2">Sources</h5>
                                <ul className="space-y-1">
                                  {verseDetail.context.sources.map((source, i) => (
                                    <li key={i} className="text-xs text-text-secondary">
                                      <span className="text-primary">•</span> {source.name}
                                      {source.author && <span className="text-text-muted"> — {source.author}</span>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-text-muted text-center py-4">
                            Pas de contexte disponible pour ce verset
                          </p>
                        )}
                      </TabsContent>
                      
                      <TabsContent value="tafsir" className="mt-4 max-h-[60vh] overflow-y-auto pr-1">
                        <TafsirPanel
                          items={verseDetail.tafsir || []}
                          legacyEditorial={verseDetail.tafsirs || []}
                          defaultLanguage={userPrefs.default_language || 'fr'}
                          preferredSourceSlugs={userPrefs.preferred_source_slugs || []}
                          verseKey={`${selectedVerse.surah_number}:${selectedVerse.verse_number}`}
                        />
                      </TabsContent>
                      
                      <TabsContent value="hadith" className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto">
                        {/* Legacy Al-Fatiha editorial hadiths */}
                        {verseDetail.hadiths && verseDetail.hadiths.length > 0 && (
                          <div className="space-y-3">
                            {verseDetail.hadiths.map((hadith, i) => (
                              <div key={i} className="p-3 bg-surface-elevated rounded-lg border-l-2 border-amber-500/50">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <ContentBadge type="hadith" />
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    hadith.grade === 'sahih' ? 'bg-emerald-900/30 text-emerald-400' :
                                    hadith.grade === 'hasan' ? 'bg-blue-900/30 text-blue-400' :
                                    'bg-orange-900/30 text-orange-400'
                                  }`}>
                                    {hadith.grade === 'sahih' ? 'Sahih (Authentique)' :
                                     hadith.grade === 'hasan' ? 'Hasan (Bon)' : "Da'if (Faible)"}
                                  </span>
                                  {hadith.hadith_number && (
                                    <span className="text-[10px] text-text-muted">n°{hadith.hadith_number}</span>
                                  )}
                                </div>
                                <p className="text-sm text-text-secondary leading-relaxed mb-3">{hadith.translation_french || hadith.translation_english}</p>
                                {hadith.narrator && (
                                  <p className="text-xs text-text-muted pt-2 border-t border-border/50">
                                    <span className="text-primary">Rapporté par :</span> {hadith.narrator}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Semantic-link-powered related hadiths + events + prophets */}
                        <RelatedContent
                          entityType="ayah"
                          entityId={`${verseDetail.verse.surah_number}:${verseDetail.verse.verse_number}`}
                          title={verseDetail.hadiths?.length ? 'Autres contenus reliés' : null}
                          types={['hadith', 'event', 'prophet', 'legacy_hadith']}
                        />
                      </TabsContent>
                    </Tabs>
                  ) : null}
                </GlassCard>
              ) : (
                <GlassCard className="text-center py-12">
                  <BookMarked className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary">Cliquez sur un verset pour voir les détails</p>
                  <p className="text-sm text-text-muted mt-2">
                    Contexte, tafsir et hadiths associés
                  </p>
                </GlassCard>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Lexical word-analysis panel */}
      <QuranWordAnalysisPanel
        open={!!lexicalSelection}
        onClose={() => setLexicalSelection(null)}
        surahNumber={Number(surahNumber)}
        ayahKey={lexicalSelection?.ayahKey}
        wordIndex={lexicalSelection?.wordIndex}
        initialSegments={lexicalSelection?.segments}
        initialDisplayText={lexicalSelection?.displayText}
      />
    </div>
  );
};

export default SurahDetail;
